// src/pages/locations/components/LocationFilters.tsx
import React from "react";
import { FiSearch } from "react-icons/fi";

import type { LocationCountry, LocationRegion } from "@/api/features/locations/types";

import { CountryPicker } from "./CountryPicker";

/* ------------------------------- Search ------------------------------- */

export const SearchField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, disabled = false }) => (
  <div className="relative">
    <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={placeholder}
      className="h-[38px] w-56 rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:opacity-60"
    />
  </div>
);

/* ------------------------------ Dropdown ------------------------------ */

const FilterShell: React.FC<{
  label: string;
  htmlFor?: string;
  required?: boolean;
  width: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, required = false, width, children }) => (
  <div className={width}>
    <label
      htmlFor={htmlFor}
      className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500"
    >
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const selectClass =
  "h-[38px] w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 pr-9 text-sm text-gray-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:opacity-60";

/**
 * The same type-ahead used inside the forms — picking a country works
 * identically whether you are filtering a list or filling in a record.
 */
export const CountryFilter: React.FC<{
  value: string;
  onChange: (iso2: string) => void;
  countries: LocationCountry[];
  disabled?: boolean;
}> = ({ value, onChange, countries, disabled = false }) => (
  <FilterShell
    label="Country"
    htmlFor="locations-country-filter"
    required
    width="w-[240px]"
  >
    <CountryPicker
      id="locations-country-filter"
      variant="filter"
      allowClear
      value={value}
      onChange={onChange}
      countries={countries}
      disabled={disabled}
      placeholder="Search a country…"
    />
  </FilterShell>
);

export const RegionFilter: React.FC<{
  value: string;
  onChange: (v: string) => void;
  regions: LocationRegion[];
  disabled?: boolean;
}> = ({ value, onChange, regions, disabled = false }) => (
  <FilterShell label="Region" htmlFor="locations-region-filter" width="w-[240px]">
    <div className="relative">
      <select
        id="locations-region-filter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={selectClass}
      >
        <option value="">All regions</option>
        <option value="null">Cities without a region</option>
        {regions.map((r) => (
          <option key={r.id} value={String(r.id)}>
            {r.name}
            {r.code ? ` (${r.code})` : ""}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
        ▾
      </span>
    </div>
  </FilterShell>
);

/* ------------------------- Small shared blocks ------------------------ */

export const RefreshingStrip: React.FC<{ show: boolean }> = ({ show }) =>
  show ? (
    <div className="mb-3 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-700">
      Refreshing list…
    </div>
  ) : null;

export const SelectCountryNotice: React.FC<{ entity: string }> = ({ entity }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
    Select a country to view {entity}.
  </div>
);
